import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { mensagemDaFuncao } from '@/lib/erroDaFuncao';
import { depoisDoClique } from '@/components/kanban/triagem/acaoTriagem';
import type { FaltaNumeroHa } from './NumeroLinhaHaDialog';

interface FinalizarPayload {
  item_id: string;
  pedido_id: string;
  cnpj: string;
  razao_social: string;
  telefone: string;
  email?: string;
  cliente_id?: string;
  status_anterior_id: string;
  coluna_anterior: string;
  quadro_anterior: string;
  acao: 'finalizar_ativo' | 'pn_concluido';
  linhas_numeros?: string[];
}

/** O backend recusou: HA ainda com "Número Novo" (Douglas 30/09/2026). */
class HaSemNumero extends Error {
  constructor(message: string, readonly faltas: FaltaNumeroHa[], readonly produto: string) {
    super(message);
  }
}

async function recusaHaSemNumero(error: unknown): Promise<HaSemNumero | null> {
  const contexto = (error as { context?: unknown })?.context;
  if (!(contexto instanceof Response)) return null;
  try {
    const corpo = await contexto.clone().json();
    if (corpo?.codigo !== 'HA_SEM_NUMERO' || !Array.isArray(corpo.faltas)) return null;
    return new HaSemNumero(String(corpo.error || ''), corpo.faltas, String(corpo.produto || ''));
  } catch {
    return null;
  }
}

interface PendenteNumero {
  payload: FinalizarPayload;
  faltas: FaltaNumeroHa[];
  produto: string;
  erro: string | null;
}

const SEM_FALTAS: FaltaNumeroHa[] = [];

export function useFinalizarPedidoAtivo() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pendenteNumero, setPendenteNumero] = useState<PendenteNumero | null>(null);

  const mutation = useMutation({
    mutationFn: async (payload: FinalizarPayload) => {
      const { data, error } = await supabase.functions.invoke('triagem-ativacao', {
        body: payload,
      });

      if (error) {
        const recusa = await recusaHaSemNumero(error);
        if (recusa) throw recusa;
        throw new Error(await mensagemDaFuncao(error, 'Tente novamente.'));
      }
      return data;
    },
    onSuccess: (_, variables) => {
      setPendenteNumero(null);
      const label = variables.acao === 'pn_concluido' ? '(PN) Concluído' : 'Finalizado Pedido (100%)';
      toast({
        title: label,
        description: 'Item finalizado. WhatsApp, Email e Lead de Qualidade criados.',
      });
      void depoisDoClique(queryClient, ['triagem-ativacao', 'triagem-nota-fiscal', 'triagem-portabilidade-concluida'],
        { pedidoId: variables.pedido_id, itens: [variables.item_id] }, 'sai');
    },
    onError: (error, variables) => {
      if (error instanceof HaSemNumero) {
        setPendenteNumero({
          payload: variables,
          faltas: error.faltas,
          produto: error.produto,
          erro: variables.linhas_numeros ? error.message : null,
        });
        return;
      }
      console.error('Erro ao finalizar pedido:', error);
      const msg = error instanceof Error ? error.message : '';
      const isAuthError = msg.toLowerCase().includes('refresh_token') ||
                          msg.toLowerCase().includes('invalid refresh') ||
                          msg.toLowerCase().includes('jwt expired') ||
                          msg.toLowerCase().includes('not authenticated');
      toast({
        title: isAuthError ? 'Sessão expirada' : 'Erro ao finalizar',
        description: isAuthError
          ? 'Sua sessão expirou. Por favor, faça login novamente.'
          : msg || 'Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  return {
    finalizarPedido: mutation.mutate,
    /** Para a fila deixar ocupado só o pedido clicado (`useLinhasOcupadas`). */
    finalizarPedidoAsync: mutation.mutateAsync,
    isFinalizando: mutation.isPending,
    numeroHaDialog: {
      open: !!pendenteNumero,
      onOpenChange: (aberto: boolean) => { if (!aberto) setPendenteNumero(null); },
      razaoSocial: pendenteNumero?.payload.razao_social || '',
      produto: pendenteNumero?.produto || '',
      faltas: pendenteNumero?.faltas || SEM_FALTAS,
      erro: pendenteNumero?.erro || null,
      isLoading: mutation.isPending,
      onConfirm: (numeros: string[]) => {
        if (pendenteNumero) mutation.mutate({ ...pendenteNumero.payload, linhas_numeros: numeros });
      },
    },
  };
}
